import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import {
  ArrowLeft,
  BadgeDollarSign,
  CalendarDays,
  ClipboardList,
  FileText,
  Landmark,
} from 'lucide-react';
import { axiomTheme } from '../../styles/designTokens';
import { formatAmountWithCommas } from '../../utils/amountUtils';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  color: ${axiomTheme.color.text.secondary};
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const BackButton = styled.button`
  border: 1px solid rgba(96, 165, 250, 0.28);
  background: rgba(15, 23, 42, 0.72);
  color: ${axiomTheme.color.text.primary};
  border-radius: 10px;
  padding: 0.65rem 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 850;
  cursor: pointer;

  &:hover {
    border-color: rgba(96, 165, 250, 0.55);
    background: rgba(30, 41, 59, 0.86);
  }
`;

const StatusPill = styled.div`
  border: 1px solid rgba(148, 163, 184, 0.26);
  background: rgba(30, 41, 59, 0.78);
  color: ${axiomTheme.color.text.primary};
  border-radius: 999px;
  padding: 0.45rem 0.85rem;
  font-size: 0.74rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 900;
`;

const Header = styled.section`
  border: 1px solid rgba(51, 65, 85, 0.78);
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.82) 0%, rgba(8, 13, 23, 0.92) 100%);
  border-radius: 14px;
  padding: 1.35rem;
`;

const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #7dd3fc;
  font-size: 0.72rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 900;
  margin-bottom: 0.55rem;
`;

const Title = styled.h1`
  color: ${axiomTheme.color.text.primary};
  font-size: 1.55rem;
  line-height: 1.15;
  letter-spacing: 0;
  margin: 0;
`;

const Subline = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-top: 0.7rem;
  color: ${axiomTheme.color.text.muted};
  font-size: 0.86rem;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  border: 1px solid rgba(100, 116, 139, 0.28);
  border-radius: 7px;
  background: rgba(15, 23, 42, 0.72);
  padding: 0.2rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 850;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.85rem;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Metric = styled.div`
  border: 1px solid rgba(51, 65, 85, 0.78);
  background: rgba(15, 23, 42, 0.72);
  border-radius: 12px;
  padding: 1rem;
  min-width: 0;
`;

const MetricLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  color: ${axiomTheme.color.text.faint};
  font-size: 0.7rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 900;
  margin-bottom: 0.5rem;
`;

const MetricValue = styled.div`
  color: ${axiomTheme.color.text.primary};
  font-size: 1.1rem;
  line-height: 1.2;
  font-weight: 900;
  overflow-wrap: anywhere;
`;

const Panel = styled.section`
  border: 1px solid rgba(51, 65, 85, 0.78);
  background: rgba(15, 23, 42, 0.72);
  border-radius: 14px;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  border-bottom: 1px solid rgba(51, 65, 85, 0.72);
  padding: 1rem 1.1rem;
  color: ${axiomTheme.color.text.primary};
  font-size: 0.95rem;
  font-weight: 900;
  display: flex;
  align-items: center;
  gap: 0.55rem;
`;

const PanelBody = styled.div`
  padding: 1rem 1.1rem;
  color: ${axiomTheme.color.text.secondary};
  white-space: pre-wrap;
  line-height: 1.55;
  font-size: 0.9rem;
`;

const ErrorBox = styled.div`
  border: 1px solid rgba(248, 113, 113, 0.35);
  background: rgba(127, 29, 29, 0.18);
  color: #fecaca;
  border-radius: 12px;
  padding: 1rem;
  font-weight: 800;
`;

const formatMoney = (value: unknown) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return `$${formatAmountWithCommas(n)}`;
};

const formatPlain = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const eventLabel = (transactionType?: string, eventType?: string) => {
  const txn = (transactionType || '').toUpperCase();
  const event = (eventType || '').toUpperCase();
  if (txn === 'COUPON' || event === 'ACCRUAL') return 'Coupon / Accrual';
  if (txn === 'REVALUATION' || event === 'MTM') return 'Revaluation / MTM';
  if (event === 'RATE_RESET') return 'Rate Reset';
  if (event === 'AMENDMENT') return 'Amendment';
  if (event === 'PIK') return 'PIK Election';
  return event || txn || 'Lifecycle Event';
};

const accountingInterpretation = (transaction: any) => {
  const txn = (transaction?.transactionType || '').toUpperCase();
  const event = (transaction?.eventType || '').toUpperCase();
  if (txn === 'COUPON' || event === 'ACCRUAL') {
    return 'Cash income / accrual event. This row is posted to the ledger as an accounting lifecycle event and does not represent an acquisition, drawdown, or paydown.';
  }
  if (txn === 'REVALUATION' || event === 'MTM') {
    return 'Mark-to-market lifecycle event. Quantity carries the fair value after the mark when present; amount carries the impairment or revaluation amount. No principal movement is booked from this detail view.';
  }
  return 'Posted credit lifecycle event. This detail view preserves the ledger event without remapping it into a principal trade workflow.';
};

const CreditPostedEventDetailScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const state = (location.state || {}) as any;
  const transactionId = state.id || state.transactionId || searchParams.get('id');
  const [transaction, setTransaction] = useState<any>(state.transaction || null);
  const [loading, setLoading] = useState(!state.transaction);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!transactionId || transaction) return;
    let active = true;
    setLoading(true);
    axios.get(`/api/trade-transactions/${transactionId}`)
      .then(res => {
        if (active) setTransaction(res.data);
      })
      .catch(err => {
        if (active) setError(err?.response?.data?.message || err?.message || 'Unable to load posted event.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [transactionId, transaction]);

  const title = useMemo(
    () => eventLabel(transaction?.transactionType, transaction?.eventType),
    [transaction]
  );

  if (loading) {
    return (
      <Page>
        <TopBar>
          <BackButton onClick={() => navigate('/blotter')}><ArrowLeft size={16} /> Blotter</BackButton>
        </TopBar>
        <Header>
          <Eyebrow><FileText size={15} /> Posted Credit Event</Eyebrow>
          <Title>Loading event</Title>
        </Header>
      </Page>
    );
  }

  if (error || !transaction) {
    return (
      <Page>
        <TopBar>
          <BackButton onClick={() => navigate('/blotter')}><ArrowLeft size={16} /> Blotter</BackButton>
        </TopBar>
        <ErrorBox>{error || 'Posted event not found.'}</ErrorBox>
      </Page>
    );
  }

  return (
    <Page>
      <TopBar>
        <BackButton onClick={() => navigate('/blotter')}><ArrowLeft size={16} /> Blotter</BackButton>
        <StatusPill>{formatPlain(transaction.status)}</StatusPill>
      </TopBar>

      <Header>
        <Eyebrow><FileText size={15} /> Posted Credit Event</Eyebrow>
        <Title>{title} #{transaction.id}</Title>
        <Subline>
          <Tag>{formatPlain(transaction.ticketType)}</Tag>
          <Tag>{formatPlain(transaction.transactionType)}</Tag>
          <Tag>{formatPlain(transaction.eventType)}</Tag>
          {transaction.sourceRef && <Tag>{transaction.sourceRef}</Tag>}
        </Subline>
      </Header>

      <Grid>
        <Metric>
          <MetricLabel><Landmark size={14} /> Security</MetricLabel>
          <MetricValue>{formatPlain(transaction.securityName || transaction.security || transaction.cusip)}</MetricValue>
        </Metric>
        <Metric>
          <MetricLabel><ClipboardList size={14} /> CUSIP</MetricLabel>
          <MetricValue>{formatPlain(transaction.cusip)}</MetricValue>
        </Metric>
        <Metric>
          <MetricLabel><FileText size={14} /> Portfolio</MetricLabel>
          <MetricValue>{formatPlain(transaction.portfolioName || transaction.portfolio)}</MetricValue>
        </Metric>
        <Metric>
          <MetricLabel><CalendarDays size={14} /> Trade / Settle</MetricLabel>
          <MetricValue>{formatPlain(transaction.tradeDate)} / {formatPlain(transaction.settleDate)}</MetricValue>
        </Metric>
        <Metric>
          <MetricLabel><BadgeDollarSign size={14} /> Amount</MetricLabel>
          <MetricValue>{formatMoney(transaction.amount)}</MetricValue>
        </Metric>
        <Metric>
          <MetricLabel><BadgeDollarSign size={14} /> Quantity</MetricLabel>
          <MetricValue>{formatMoney(transaction.quantity)}</MetricValue>
        </Metric>
        <Metric>
          <MetricLabel><BadgeDollarSign size={14} /> Price</MetricLabel>
          <MetricValue>{formatPlain(transaction.price)}</MetricValue>
        </Metric>
        <Metric>
          <MetricLabel><BadgeDollarSign size={14} /> Net Amount</MetricLabel>
          <MetricValue>{formatMoney(transaction.netAmount)}</MetricValue>
        </Metric>
      </Grid>

      <Panel>
        <PanelHeader><FileText size={16} /> Accounting Interpretation</PanelHeader>
        <PanelBody>{accountingInterpretation(transaction)}</PanelBody>
      </Panel>

      {transaction.notes && (
        <Panel>
          <PanelHeader><FileText size={16} /> Notes</PanelHeader>
          <PanelBody>{transaction.notes}</PanelBody>
        </Panel>
      )}
    </Page>
  );
};

export default CreditPostedEventDetailScreen;
